/**
 * アコーディオン初期化関数
 * 9行以上のテキストがある場合のみアコーディオン化し、3点リーダーを表示します。
 */
const initAccordion = (): void => {
  const containers = document.querySelectorAll<HTMLElement>(
    '[data-role="ac-content"]'
  );

  containers.forEach((container) => {
    const openBtn = container.querySelector<HTMLElement>(
      '[data-role="ac-open"]'
    );
    const closeBtn = container.querySelector<HTMLElement>(
      '[data-role="ac-close"]'
    );
    const box = container.querySelector<HTMLElement>('[data-role="ac-box"]');
    const inner = container.querySelector<HTMLElement>('.inner');

    if (!openBtn || !closeBtn || !box || !inner) return;

    /**
     * 行数を判定し、スタイルを適用する内部関数
     */
    const checkLineCount = () => {
      // タブなどで非表示（高さ0）の場合は判定できないためスキップ
      if (inner.offsetHeight === 0) return;

      // すでに開いている場合は再判定しない
      if (container.classList.contains('is-open')) return;

      // 計測用にスタイルを一時リセット（絶対配置にしてレイアウトを崩さず測る）
      const originalInnerStyle = inner.getAttribute('style') || '';
      inner.style.display = 'block';
      inner.style.webkitLineClamp = 'none';
      inner.style.position = 'absolute';
      inner.style.visibility = 'hidden';

      const fullHeight = inner.scrollHeight;
      const style = window.getComputedStyle(inner);
      const lineHeight =
        parseFloat(style.lineHeight) || parseInt(style.fontSize) * 1.5;
      const lineCount = Math.round(fullHeight / lineHeight);

      // スタイルを元の状態に戻す
      inner.setAttribute('style', originalInnerStyle);

      // 【判定】9行より多い場合のみアコーディオンを有効にする
      if (lineCount > 9) {
        // 9行分の高さからボタンエリア等を考慮して調整
        // ※-90pxだと短すぎる場合は、ここを +20 などに調整してください
        const targetHeight = lineHeight * 9;

        box.style.maxHeight = `${targetHeight}px`;
        box.style.display = 'block';
        inner.style.display = '-webkit-box';
        inner.style.webkitLineClamp = '9';
        inner.style.webkitBoxOrient = 'vertical';

        openBtn.style.display = 'block';
        closeBtn.style.display = 'none';
      } else {
        // 9行以下の場合はボタンを隠し、制限を完全に解除
        box.style.maxHeight = 'none';
        inner.style.display = 'block';
        inner.style.webkitLineClamp = 'none';
        openBtn.style.display = 'none';
        closeBtn.style.display = 'none';
      }
    };

    // 【タブ対策】Intersection Observerで要素が可視状態になったら判定を実行
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            checkLineCount();
          }
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(container);

    // リサイズ時にも再判定
    window.addEventListener('resize', checkLineCount);

    // --- イベントリスナー ---

    // 開く処理
    openBtn.addEventListener('click', () => {
      container.classList.add('is-open');

      // innerの制限を解除（これを行わないと9行で止まる）
      inner.style.display = 'block';
      inner.style.webkitLineClamp = 'none';
      inner.style.webkitBoxOrient = 'unset';

      // boxの高さをコンテンツ全体の高さに合わせる
      box.style.maxHeight = `${box.scrollHeight}px`;

      openBtn.style.display = 'none';
      closeBtn.style.display = 'block';

      // アニメーション完了後にmax-heightを解除
      setTimeout(() => {
        if (container.classList.contains('is-open')) {
          box.style.maxHeight = 'none';
        }
      }, 400);
    });

    // 閉じる処理
    closeBtn.addEventListener('click', () => {
      const style = window.getComputedStyle(inner);
      const lineHeight = parseFloat(style.lineHeight) || 24;

      container.classList.remove('is-open');

      // 数値指定に戻してアニメーションを有効にする
      box.style.maxHeight = `${box.scrollHeight}px`;

      requestAnimationFrame(() => {
        // 制限状態に戻す
        inner.style.display = '-webkit-box';
        inner.style.webkitLineClamp = '9';
        inner.style.webkitBoxOrient = 'vertical';

        const targetHeight = lineHeight * 9;
        box.style.maxHeight = `${targetHeight}px`;

        openBtn.style.display = 'block';
        closeBtn.style.display = 'none';
      });

      // スムーズスクロールで位置を戻す
      container.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });
};

// DOM読み込み完了時に実行
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAccordion);
} else {
  initAccordion();
}
